const SIGNAGE_TOKEN_PARAM = /([?&]t=)[^&#\s]+/gi;
const ENCODED_SIGNAGE_TOKEN_PARAM = /((?:%3F|%26)t%3D).*?(?=%26|$)/gi;
const SIGNAGE_SCREEN_PARAM = /([?&]screen_id=)[^&#\s]+/gi;
const ENCODED_SIGNAGE_SCREEN_PARAM = /((?:%3F|%26)screen_id%3D).*?(?=%26|$)/gi;
const SIGNAGE_CLAIM_PARAM = /([?&]claim=)[^&#\s]+/gi;
const ENCODED_SIGNAGE_CLAIM_PARAM = /((?:%3F|%26)claim%3D).*?(?=%26|$)/gi;
const REFRESH_TOKEN_PARAM = /([#?&]rt=)[^&#\s]+/gi;
const ENCODED_REFRESH_TOKEN_PARAM = /((?:%23|%3F|%26)rt%3D).*?(?=%26|$)/gi;
const GENERIC_BEARER_PARAM = /([#?&](?:token|access_token)=)[^&#\s]+/gi;
const ENCODED_GENERIC_BEARER_PARAM = /((?:%23|%3F|%26)(?:token|access_token)%3D).*?(?=%26|$)/gi;
// The visitor's own page link (`/v/<customerId>#key=…`). The page scrubs it from
// the address bar before bootstrap and PostHog is not started there; this is the
// belt to those braces, should a URL carrying one ever reach an event.
const VISITOR_LINK_KEY_PARAM = /([#?&]key=)[^&#\s]+/gi;
const ENCODED_VISITOR_LINK_KEY_PARAM = /((?:%23|%3F|%26)key%3D).*?(?=%26|$)/gi;
// Free text a person typed into a search box and the page kept in its URL
// (`?q=` on the operator console's Activity tab and the admin map, `?search=`
// elsewhere, `?prefill=` for a question handed to Ask). It can hold an email
// address or a name, and PostHog sends the whole URL (`$current_url`,
// `$referrer`) with every later event.
const SEARCH_TEXT_PARAM = /([#?&](?:q|search|prefill)=)[^&#\s]+/gi;
const ENCODED_SEARCH_TEXT_PARAM = /((?:%23|%3F|%26)(?:q|search|prefill)%3D).*?(?=%26|$)/gi;

/**
 * Remove signed signage bearer tokens — and free text typed into a search
 * box — from URLs before third-party telemetry.
 */
export function redactSignageTokenString(value: string): string {
  return value
    .replace(SIGNAGE_TOKEN_PARAM, '$1[REDACTED]')
    .replace(ENCODED_SIGNAGE_TOKEN_PARAM, '$1[REDACTED]')
    .replace(SIGNAGE_SCREEN_PARAM, '$1[REDACTED]')
    .replace(ENCODED_SIGNAGE_SCREEN_PARAM, '$1[REDACTED]')
    .replace(SIGNAGE_CLAIM_PARAM, '$1[REDACTED]')
    .replace(ENCODED_SIGNAGE_CLAIM_PARAM, '$1[REDACTED]')
    .replace(REFRESH_TOKEN_PARAM, '$1[REDACTED]')
    .replace(ENCODED_REFRESH_TOKEN_PARAM, '$1[REDACTED]')
    .replace(GENERIC_BEARER_PARAM, '$1[REDACTED]')
    .replace(ENCODED_GENERIC_BEARER_PARAM, '$1[REDACTED]')
    .replace(VISITOR_LINK_KEY_PARAM, '$1[REDACTED]')
    .replace(ENCODED_VISITOR_LINK_KEY_PARAM, '$1[REDACTED]')
    .replace(SEARCH_TEXT_PARAM, '$1[REDACTED]')
    .replace(ENCODED_SEARCH_TEXT_PARAM, '$1[REDACTED]');
}

/**
 * Recursively sanitise PostHog event properties (including snapshot payloads).
 * Event properties are JSON-like; depth is capped as defence against malformed
 * or cyclic plugin payloads.
 */
export function redactSignageTokens<T>(value: T, depth = 0, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') {
    return redactSignageTokenString(value) as T;
  }
  if (value == null || typeof value !== 'object' || depth >= 32) return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactSignageTokens(item, depth + 1, seen)) as T;
  }

  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = redactSignageTokens(item, depth + 1, seen);
  }
  return out as T;
}
